import React, { useState } from "react";
import { Box, Typography, Stack } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { colors, shadows } from "../source/source.styles";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import DescriptionIcon from "@mui/icons-material/Description";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CircleIcon from "@mui/icons-material/Circle";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";

const FunnelStageConversion = ({ data }) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const totalLeads = data.lead || 1;

  // Stage data
  const stages = [
    {
      name: "Leads",
      subtitle: "Grade Stage",
      count: data.lead || 0,
      color: primaryColor,
      bgColor: alpha(primaryColor, 0.1),
      icon: <PersonIcon sx={{ fontSize: 24, color: primaryColor }} />,
      dropped: data.grade?.dropped || 0,
      conversionRate: 100,
      barWidth: 100,
    },
    {
      name: "Pre-Consult",
      subtitle: "Qualified & Booked",
      count: data.preConsultation || 0,
      color: "#3B82F6",
      bgColor: "#DBEAFE",
      icon: <PhoneIcon sx={{ fontSize: 24, color: "#3B82F6" }} />,
      dropped: data.preConsult?.dropped || 0,
      conversionRate: totalLeads > 0 ? Math.round((data.preConsultation / totalLeads) * 100) : 0,
      barWidth: totalLeads > 0 ? Math.max(20, (data.preConsultation / totalLeads) * 100) : 0,
    },
    {
      name: "Post-Consult",
      subtitle: "Proposal & Negotiation",
      count: data.postConsultation || 0,
      color: "#3B82F6",
      bgColor: "#DBEAFE",
      icon: <DescriptionIcon sx={{ fontSize: 24, color: "#3B82F6" }} />,
      dropped: data.postConsult?.dropped || 0,
      conversionRate: totalLeads > 0 ? Math.round((data.postConsultation / totalLeads) * 100) : 0,
      barWidth: totalLeads > 0 ? Math.max(20, (data.postConsultation / totalLeads) * 100) : 0,
    },
    {
      name: "Won",
      subtitle: "Converted Deals",
      count: data.won || 0,
      color: "#10B981",
      bgColor: "#ECFDF5",
      icon: <EmojiEventsIcon sx={{ fontSize: 24, color: "#10B981" }} />,
      dropped: 0,
      conversionRate: totalLeads > 0 ? Math.round((data.won / totalLeads) * 100) : 0,
      barWidth: totalLeads > 0 ? Math.max(20, (data.won / totalLeads) * 100) : 0,
    },
  ];

  // Dropped data for pie chart
  const droppedData = [
    {
      name: "Grade Stage",
      color: primaryColor,
      dropped: data.grade?.dropped || 0,
    },
    {
      name: "Pre-Consult",
      color: "#3B82F6",
      dropped: data.preConsult?.dropped || 0,
    },
    {
      name: "Post-Consult",
      color: "#3B82F6",
      dropped: data.postConsult?.dropped || 0,
    },
  ].filter(d => d.dropped > 0);

  const totalAllDropped = stages.reduce((sum, s) => sum + s.dropped, 0);
  const netConversionRate = totalLeads > 0 ? Math.round(((data.won || 0) / totalLeads) * 100) : 0;

  // Pie chart component
  const DroppedPieChart = () => {
    const [activeIndex, setActiveIndex] = useState(null);

    if (droppedData.length === 0) {
      return (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
            No dropped leads in this period
          </Typography>
        </Box>
      );
    }

    const CustomTooltip = ({ active, payload }) => {
      if (!active || !payload?.length) return null;
      const data = payload[0].payload;
      const percentage = ((data.dropped / totalAllDropped) * 100).toFixed(1);

      return (
        <Box
          sx={{
            bgcolor: "#fff",
            px: 2,
            py: 1.5,
            borderRadius: 2,
            boxShadow: "0 8px 24px rgba(0,0,0,.15)",
            border: `1px solid ${colors.borderLight}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
            <CircleIcon sx={{ fontSize: 10, color: data.color }} />
            <Typography fontWeight={600} fontSize={13}>
              {data.name}
            </Typography>
          </Stack>
          <Typography fontSize={12} color="text.secondary">
            {data.dropped} dropped ({percentage}%)
          </Typography>
        </Box>
      );
    };

    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3, width: "100%", py: 2 }}>
        {/* Donut Chart */}
        <Box sx={{ position: "relative", width: 180, height: 180, flexShrink: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={droppedData}
                dataKey="dropped"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={45}
                outerRadius={75}
                paddingAngle={2}
                onMouseEnter={(_, index) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                {droppedData.map((entry, index) => (
                  <Cell
                    key={index}
                    fill={entry.color}
                    opacity={activeIndex === null || activeIndex === index ? 1 : 0.4}
                    stroke="#fff"
                    strokeWidth={activeIndex === index ? 2 : 1}
                  />
                ))}
              </Pie>
              <Tooltip
                content={<CustomTooltip />}
                position={{ x: 0, y: -50 }}
                wrapperStyle={{ zIndex: 10 }}
              />
            </PieChart>
          </ResponsiveContainer>

          {/* Center Text */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              pointerEvents: "none",
            }}
          >
            <Typography
              sx={{
                fontSize: 32,
                fontWeight: 700,
                color: colors.danger,
                lineHeight: 1,
              }}
            >
              {totalAllDropped}
            </Typography>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                color: colors.textMuted,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                mt: 0.5,
              }}
            >
              DROPPED
            </Typography>
          </Box>
        </Box>

        {/* Legend */}
        <Stack spacing={2} sx={{ flex: 1 }}>
          {droppedData.map((item, index) => {
            const percentage = ((item.dropped / totalAllDropped) * 100).toFixed(1);
            return (
              <Stack
                key={index}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  cursor: "pointer",
                  opacity: activeIndex === null || activeIndex === index ? 1 : 0.5,
                  transition: "opacity 0.2s",
                }}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <CircleIcon sx={{ fontSize: 10, color: item.color }} />
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: colors.textPrimary,
                    }}
                  >
                    {item.name}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: colors.danger,
                    }}
                  >
                    {item.dropped}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: colors.textMuted,
                    }}
                  >
                    ({percentage}%)
                  </Typography>
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.5,
              color: colors.textMuted,
              textTransform: "uppercase",
              mb: 0.5,
            }}
          >
            Funnel Stage Wise Conversion
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: colors.textSecondary,
            }}
          >
            Track lead progression and dropoff through each stage
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 3 }}>
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontSize: 11, color: colors.textMuted, textTransform: "uppercase" }}>
              Net Conversion
            </Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: colors.success }}>
              {netConversionRate}%
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontSize: 11, color: colors.textMuted, textTransform: "uppercase" }}>
              Total Dropped
            </Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: colors.danger }}>
              {totalAllDropped}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Two Charts Grid */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>

        {/* CHART 1: Funnel Progress Chart */}
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: colors.bgPrimary,
            border: `1px solid ${colors.borderLight}`,
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: colors.textPrimary,
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <ArrowForwardIcon sx={{ fontSize: 16, color: colors.success }} />
            Conversion Funnel
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {stages.map((stage, index) => {
              const prevCount = index > 0 ? stages[index - 1].count : stage.count;
              const dropoff = prevCount - stage.count;
              const dropoffPercent = prevCount > 0 ? Math.round((dropoff / prevCount) * 100) : 0;

              return (
                <Box key={stage.name}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: stage.bgColor, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {stage.icon}
                </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: colors.textPrimary }}>
                        {stage.name}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: stage.color }}>
                      {stage.count.toLocaleString()}
                    </Typography>
                  </Box>

                  {/* Bar */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 7.5 }}>
                    <Box sx={{ flex: 1, height: 24, position: "relative", bgcolor: "#FFFFFF", borderRadius: 1 }}>
                      <Box
                        sx={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          height: "100%",
                          width: `${stage.barWidth}%`,
                          bgcolor: stage.color,
                          borderRadius: 1,
                          transition: "width 0.5s ease",
                        }}
                      />
                    </Box>
                    {index > 0 && (
                      <Box sx={{ minWidth: 50, textAlign: "right" }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: dropoffPercent > 20 ? colors.danger : colors.textSecondary }}>
                          {dropoffPercent > 0 ? `-${dropoffPercent}%` : "-"}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* CHART 2: Dropped Leads Pie Chart */}
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: colors.bgPrimary,
            border: `1px solid ${colors.borderLight}`,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: colors.textPrimary,
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <TrendingDownIcon sx={{ fontSize: 16, color: colors.danger }} />
            Dropped Leads Analysis
          </Typography>

          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <DroppedPieChart />
          </Box>
        </Box>
      </Box>

      {/* Legend */}
      <Box
        sx={{
          mt: 2.5,
          pt: 2,
          borderTop: `1px solid ${colors.borderLight}`,
          display: "flex",
          gap: 4,
          justifyContent: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: primaryColor }} />
          <Typography sx={{ fontSize: 11, color: colors.textSecondary }}>
            Active Leads
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: "#EF4444" }} />
          <Typography sx={{ fontSize: 11, color: colors.textSecondary }}>
            Dropped Leads
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <TrendingDownIcon sx={{ fontSize: 12, color: colors.danger }} />
          <Typography sx={{ fontSize: 11, color: colors.textSecondary }}>
            Stage Dropoff %
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default FunnelStageConversion;
